/**
 * Trình dựng luồng Tự động hoá (React Flow) trên API/engine thật.
 * Khối: Kích hoạt · Điều kiện · Chờ · Hành động (+ Ghi chú của luồng cũ).
 * Cột trái thêm khối, cột phải cấu hình khối đang chọn. "Chạy thử" gửi luồng
 * đang ở canvas lên backend chạy MÔ PHỎNG với một khách, tô màu từng khối.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, addEdge,
  useNodesState, useEdgesState, useReactFlow, useNodesInitialized, getNodesBounds,
  type Edge, type Connection, type NodeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { i18n } from '@/lib/i18n'
import { ArrowLeft, FlaskConical, GitBranch, Clock, History, LayoutGrid, Loader2, PanelLeftClose, PanelLeftOpen, Plus, Save, Sparkles, Undo2, WandSparkles, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { cn } from '@/lib/utils'
import { apiError } from '@/lib/api-client'
import { useMyPermissions } from '@/hooks/use-settings'
import {
  emptyFlowConfig, genNodeId, useAutomationRule, useContactAttributeFields, useTestRun, useUpdateRule, useWebhookToken, type FlowConfig,
} from '@/hooks/use-automation'
import { actionMeta, triggerMeta, truongTuPayload, type TruongDuLieu } from '@/lib/automation/catalog'
import {
  KHOANG_X, canvasToFlowConfig, flowConfigToCanvas, taoCanh,
  type BlockData, type BlockNode, type KindKhoi, type PhanGiuLai,
} from '@/lib/automation/flow-adapter'
import { KET_QUA_LABEL, lanChayTuTestRun, type LanChay } from '@/lib/automation/run-result'
import type { MauWorkflow } from '@/lib/automation/mau-workflow'
import {
  CanvasCtx, ChonKhoiDialog, ChonKichHoatDialog, ChonMauDialog, HANH_DONG_NHANH, KhoiCanvas, VongIcon, type KhoiMoi,
} from './khoi-canvas'
import { CauHinhKhoi } from './cau-hinh-khoi'
import { ChiTietLanChay } from './chi-tiet-lan-chay'
import { NhatKyDialog } from './nhat-ky-hoat-dong'
import { ChayThuDialog, type KhachThu } from './chay-thu-dialog'
import { AiComposerPanel } from './ai-composer-panel'
import type { ComposeResult } from '@/hooks/use-automation-ai'

const nodeTypes: NodeTypes = { source: KhoiCanvas, action: KhoiCanvas, condition: KhoiCanvas, delay: KhoiCanvas, note: KhoiCanvas }

/** Ảnh chụp để biết có thay đổi chưa lưu (bỏ qua màu kết quả chạy thử, vị trí được làm tròn). */
const chupNhanh = (ten: string, uuTien: number, fc: FlowConfig) => JSON.stringify({ ten, uuTien, fc })

/** Kiểm tra trước khi lưu — lỗi chặn lưu; cảnh báo chỉ nhắc. */
function kiemTra(fc: FlowConfig): { loi?: string; canhBao: string[] } {
  const canhBao: string[] = []
  if (((fc.trigger.config.filterRules as { field: string }[] | undefined) ?? []).some((r) => !r.field?.trim())) {
    return { loi: i18n.t('autoConfig:flow.validate.filterNoField'), canhBao }
  }
  for (const n of fc.nodes) {
    if (n.type === 'action' && !n.actionType) return { loi: i18n.t('autoConfig:flow.validate.actionNoType', { label: n.label }), canhBao }
    if (n.type === 'condition' && ((n.config.rules as { field: string }[] | undefined) ?? []).some((r) => !r.field?.trim())) {
      return { loi: i18n.t('autoConfig:flow.validate.ruleNoField', { label: n.label }), canhBao }
    }
    if (n.type === 'action' && actionMeta(n.actionType).sapCo) canhBao.push(i18n.t('autoConfig:flow.validate.notRunnable', { label: n.label }))
  }
  if (!fc.edges.some((e) => e.source === fc.trigger.id)) canhBao.push(i18n.t('autoConfig:flow.validate.noNext'))
  if (triggerMeta(fc.trigger.type).khongCoNguonPhat) canhBao.push(i18n.t('autoConfig:flow.validate.triggerGone'))
  return { canhBao }
}

const goiYAi = () => [
  i18n.t('autoConfig:flow.aiHints.welcome'),
  i18n.t('autoConfig:flow.aiHints.survey'),
  i18n.t('autoConfig:flow.aiHints.vip'),
  i18n.t('autoConfig:flow.aiHints.birthday'),
]

/** Ô gợi ý Trợ lý AI trên header: trông như ô tìm kiếm, placeholder đổi gợi ý liên tục; bấm để mở cột AI bên phải. */
function OAiGoiY({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('autoConfig')
  const [i, setI] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setI((v) => (v + 1) % goiYAi().length), 3500)
    return () => clearInterval(timer)
  }, [])
  return (
    <button type="button" onClick={onClick} title={t('flow.openAi')}
      className="group flex h-9 w-80 max-w-[28vw] items-center gap-2 rounded-lg border border-primary/40 bg-gradient-to-r from-primary/10 via-violet-500/10 to-fuchsia-500/10 px-2.5 text-left shadow-sm transition hover:border-primary hover:shadow-md">
      <WandSparkles className="h-4 w-4 shrink-0 text-primary group-hover:animate-pulse" />
      <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{goiYAi()[i]}</span>
      <span className="shrink-0 rounded bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">AI</span>
    </button>
  )
}

export function AutomationFlowPage() {
  return <ReactFlowProvider><Editor /></ReactFlowProvider>
}

function Editor() {
  const { t } = useTranslation('autoConfig')
  const { ruleId } = useParams<{ ruleId: string }>()
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  // Sửa / lưu / bật-tắt / chạy thử / AI dựng lại / mã webhook đều đòi `automation.update` (automation-routes,
  // automation-ai-routes). Không có quyền (hoặc đang tải quyền) → chế độ chỉ xem, ẩn nút thao tác.
  const { data: perms } = useMyPermissions()
  const chiXem = !perms?.has('automation.update')
  const { data: rule, isLoading, isError } = useAutomationRule(ruleId)
  const update = useUpdateRule()
  const testRun = useTestRun(ruleId)

  const [nodes, setNodes, onNodesChange] = useNodesState<BlockNode>([])
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([])
  const [giuLai, setGiuLai] = useState<PhanGiuLai | null>(null)
  const [ten, setTen] = useState('')
  const [uuTien, setUuTien] = useState(0)
  const [daLuu, setDaLuu] = useState('')
  const [daNap, setDaNap] = useState<string | null>(null)
  const [selId, setSelId] = useState<string | null>(null)
  const [run, setRun] = useState<LanChay | null>(null)
  const [moVet, setMoVet] = useState(false)
  const [moChayThu, setMoChayThu] = useState(false)
  const [moNhatKy, setMoNhatKy] = useState(params.get('nhatky') === '1')
  const [moMau, setMoMau] = useState(false)
  const [chonKhoi, setChonKhoi] = useState<{ sau: string | null; handle?: 'true' | 'false' } | null>(null)
  const [moKichHoat, setMoKichHoat] = useState(false)
  const [moThanh, setMoThanh] = useState(true)
  const [moAi, setMoAi] = useState(false)
  const truocAi = useRef<FlowConfig | null>(null)
  const [coTheHoanTac, setCoTheHoanTac] = useState(false)

  const rf = useReactFlow()
  const khungRef = useRef<HTMLDivElement>(null)
  const daDoKhoi = useNodesInitialized()
  const [canFit, setCanFit] = useState(false)

  const napCanvas = useCallback((fc: FlowConfig) => {
    const g = flowConfigToCanvas(fc)
    // Khối Kích hoạt không xoá được (Delete/Backspace).
    setNodes(g.nodes.map((n) => (n.data.kind === 'source' ? { ...n, deletable: false } : n)))
    setEdges(g.edges)
    setGiuLai(g.giuLai)
    setSelId(null); setRun(null); setCanFit(true)
    return g
  }, [setNodes, setEdges])

  // Nạp rule một lần (không nạp lại khi rule đổi do bật/tắt hay lưu).
  useEffect(() => {
    if (!rule || daNap === rule.id) return
    // Engine khớp rule theo cột `trigger`, nên đó là loại kích hoạt thật của luồng.
    const fc0 = rule.flowConfig?.trigger ? rule.flowConfig : emptyFlowConfig(rule.trigger)
    const fc = { ...fc0, trigger: { ...fc0.trigger, type: rule.trigger } }
    const g = napCanvas(fc)
    setTen(rule.name)
    setUuTien(rule.priority ?? 0)
    setDaLuu(chupNhanh(rule.name, rule.priority ?? 0, canvasToFlowConfig(g.nodes, g.edges, g.giuLai)))
    setDaNap(rule.id)
  }, [rule, daNap, napCanvas])

  // fitView của ReactFlow chạy trước khi khối được đo → tự canh lại khi đã đo xong.
  useEffect(() => {
    if (!canFit || !daDoKhoi) return
    setCanFit(false)
    const el = khungRef.current; const b = getNodesBounds(rf.getNodes())
    if (!el || !b.width) { rf.fitView({ padding: 0.15, maxZoom: 1 }); return }
    const w = el.clientWidth, h = el.clientHeight
    // Không thu nhỏ quá 0,55 — nhỏ hơn thì chữ trong khối không đọc được; khi đó canh mép trái để thấy khối Kích hoạt.
    const zoom = Math.min(1, Math.max(0.55, Math.min((w - 80) / b.width, (h - 80) / b.height)))
    const vua = b.width * zoom <= w - 80
    rf.setViewport({ zoom, x: vua ? (w - b.width * zoom) / 2 - b.x * zoom : 40 - b.x * zoom, y: Math.max(24, (h - b.height * zoom) / 2) - b.y * zoom })
  }, [canFit, daDoKhoi, rf])

  const fcHienTai = useMemo(() => (giuLai && nodes.length ? canvasToFlowConfig(nodes, edges, giuLai) : null), [nodes, edges, giuLai])
  const chuaLuu = !!fcHienTai && !!daLuu && chupNhanh(ten.trim(), uuTien, fcHienTai) !== daLuu
  const triggerType = fcHienTai?.trigger.type ?? rule?.trigger ?? ''
  const kinds = useMemo(() => new Map<string, KindKhoi>(nodes.map((n) => [n.id, n.data.kind])), [nodes])
  const { data: webhook } = useWebhookToken(ruleId, triggerType === 'webhook' && !chiXem)
  const { data: ketNoi } = useContactAttributeFields()
  // Field mở rộng cho ô chọn field Điều kiện + nút "Chèn biến": trường `trigger.*` của payload webhook gần nhất,
  // cộng dữ liệu động theo kết nối tổ chức đã đồng bộ (Evotech hạng/điểm/tổng chi tiêu…).
  const truongMoRong = useMemo<TruongDuLieu[]>(
    () => [...truongTuPayload(webhook?.lastPayload), ...(ketNoi?.fields.map((f) => ({ path: f.field, label: `${f.label} (${f.sourceLabel})` })) ?? [])],
    [webhook?.lastPayload, ketNoi],
  )

  // Chặn rời trang khi còn thay đổi chưa lưu.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => chuaLuu && currentLocation.pathname !== nextLocation.pathname)
  useEffect(() => {
    if (!chuaLuu) return
    const h = (e: BeforeUnloadEvent) => { e.preventDefault() }
    window.addEventListener('beforeunload', h)
    return () => window.removeEventListener('beforeunload', h)
  }, [chuaLuu])

  // Esc: bỏ chọn khối → ẩn bảng cấu hình (bỏ qua khi đang gõ).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !selId) return
      const tgt = e.target as HTMLElement | null
      if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.isContentEditable)) return
      setSelId(null); setNodes((ns) => ns.map((n) => (n.selected ? { ...n, selected: false } : n)))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selId, setNodes])

  const onConnect = useCallback((c: Connection) => {
    if (!c.source || !c.target || c.source === c.target) return
    setEdges((eds) => addEdge(taoCanh(c.source, c.target, c.sourceHandle, genNodeId('e')), eds))
  }, [setEdges])

  if (isLoading) return <div className="p-6"><Loading label={t('flow.loading')} /></div>
  if (isError || !rule) return <div className="p-6"><ErrorState message={t('flow.notFound')} /><div className="mt-2 text-center"><Button variant="link" onClick={() => nav('/automation')}>{t('flow.backToList')}</Button></div></div>

  const sel = nodes.find((n) => n.id === selId) ?? null
  const capNhatSel = (patch: Partial<BlockData>) => setNodes((ns) => ns.map((n) => (n.id === selId ? { ...n, data: { ...n.data, ...patch } } : n)))
  const boChon = () => { setSelId(null); setNodes((ns) => ns.map((n) => (n.selected ? { ...n, selected: false } : n))) }
  const xoaSel = () => {
    if (!sel || sel.data.kind === 'source') return
    setNodes((ns) => ns.filter((n) => n.id !== sel.id))
    setEdges((es) => es.filter((e) => e.source !== sel.id && e.target !== sel.id))
    setSelId(null)
  }

  /** Thêm khối; có khối cha thì đặt bên phải và nối dây (bố cục trái → phải). */
  const them = (k: KhoiMoi, sau?: { id: string | null; handle?: 'true' | 'false' }) => {
    const cha = sau === undefined ? sel : sau.id ? nodes.find((n) => n.id === sau.id) ?? null : null
    const handle = sau === undefined ? (sel?.data.kind === 'condition' ? 'true' : undefined) : sau.handle
    const base = cha?.position ?? nodes[nodes.length - 1]?.position ?? { x: 40, y: 40 }
    let pos = { x: base.x + KHOANG_X, y: base.y + (handle === 'false' ? 320 : 0) }
    while (nodes.some((n) => Math.abs(n.position.x - pos.x) < 200 && Math.abs(n.position.y - pos.y) < 200)) pos = { x: pos.x, y: pos.y + 320 }
    const nid = genNodeId(k.kind)
    const data: BlockData = k.kind === 'action'
      ? { kind: 'action', loai: k.actionType, label: k.label ?? actionMeta(k.actionType).ten, status: 'active', config: structuredClone(k.config ?? actionMeta(k.actionType).macDinh) }
      : k.kind === 'condition'
        ? { kind: 'condition', label: 'Điều kiện' /* i18n-ignore: tên khối mặc định, lưu vào luồng */, status: 'active', config: { logic: 'and', rules: [{ field: 'contact.lifecycleStage', op: 'eq', value: '' }] } }
        : { kind: 'delay', label: 'Chờ' /* i18n-ignore: tên khối mặc định, lưu vào luồng */, status: 'active', config: { duration: 1, unit: 'hours' } }
    setNodes((ns) => [...ns.map((n) => ({ ...n, selected: false })), { id: nid, type: data.kind, position: pos, selected: true, data }])
    if (cha) setEdges((es) => [...es, taoCanh(cha.id, nid, handle, genNodeId('e'))])
    setSelId(nid)
  }

  const doiKichHoat = (type: string) => {
    setNodes((ns) => ns.map((n) => {
      if (n.data.kind !== 'source') return n
      const nhanCu = triggerMeta(n.data.loai).ten
      return { ...n, data: { ...n.data, loai: type, config: {}, label: n.data.label === nhanCu || !n.data.label ? triggerMeta(type).ten : n.data.label } }
    }))
    const src = nodes.find((n) => n.data.kind === 'source')
    if (src) setSelId(src.id)
    toast.success(t('flow.toast.triggerChanged'))
  }

  const luu = async (bat?: boolean) => {
    if (!fcHienTai) return
    const { loi, canhBao } = kiemTra(fcHienTai)
    if (loi) { toast.error(loi); return }
    const tenLuu = ten.trim() || rule.name
    try {
      await update.mutateAsync({ id: rule.id, data: { name: tenLuu, priority: uuTien, trigger: fcHienTai.trigger.type, flowVersion: 2, flowConfig: fcHienTai, ...(bat ? { enabled: true } : {}) } })
      setTen(tenLuu)
      setDaLuu(chupNhanh(tenLuu, uuTien, fcHienTai))
      toast.success(bat ? t('flow.toast.savedEnabled') : rule.enabled ? t('flow.toast.saved') : t('flow.toast.savedOff'))
      canhBao.forEach((c) => toast.warning(c))
    } catch (e) { toast.error(apiError(e)) }
  }

  const batTat = (v: boolean) => {
    update.mutate({ id: rule.id, data: { enabled: v } }, {
      onSuccess: () => toast.success(v ? (chuaLuu ? t('flow.toast.enabledUnsaved') : t('flow.toast.enabled')) : t('flow.toast.disabled')),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  const chayThu = (khach?: KhachThu) => {
    if (!fcHienTai) return
    testRun.mutate({ flowConfig: { ...fcHienTai, metadata: { ...(fcHienTai.metadata ?? {}), name: ten || rule.name } }, contactId: khach?.id }, {
      onSuccess: (kq) => {
        const r = lanChayTuTestRun(kq, kinds, khach?.ten ?? kq.testContext.contactName)
        setRun(r); setMoChayThu(false); setMoVet(true)
        const theoNode = new Map(r.steps.map((s) => [s.nodeId, s]))
        setNodes((ns) => ns.map((n) => {
          const s = theoNode.get(n.id)
          const nguon = r.ketQua === 'bo_qua' ? 'skip' : 'ok'
          return { ...n, data: { ...n.data, runStatus: n.data.kind === 'source' ? nguon : s ? s.status : 'skip', branch: s?.branch } }
        }))
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }
  const xoaVet = () => { setRun(null); setNodes((ns) => ns.map((n) => ({ ...n, data: { ...n.data, runStatus: undefined, branch: undefined } }))) }

  const apMau = (mau: MauWorkflow) => {
    napCanvas(structuredClone(mau.flow))
    if (!ten.trim() || ten === rule.name) setTen(mau.tenLuu) // tên luồng LƯU DB: giữ tiếng Việt, không theo ngôn ngữ giao diện
    toast.success(t('flow.toast.appliedTemplate', { name: mau.ten }))
  }

  const apDungAi = (kq: ComposeResult) => {
    truocAi.current = fcHienTai ? structuredClone(fcHienTai) : null
    napCanvas(kq.flowConfig)
    if (kq.name && (!ten.trim() || ten === rule.name) && rule.name.startsWith('Luồng' /* i18n-ignore: tiền tố tên luồng mặc định đã lưu */)) setTen(kq.name)
    setCoTheHoanTac(!!truocAi.current)
    toast.success(t('flow.toast.appliedAi'))
  }
  const hoanTacAi = () => {
    if (!truocAi.current) return
    napCanvas(truocAi.current); truocAi.current = null; setCoTheHoanTac(false)
    toast.success(t('flow.toast.undoneAi'))
  }

  const tm = triggerMeta(triggerType)
  const buocCuaSel = sel && run ? [...run.steps].reverse().find((s) => s.nodeId === sel.id) : undefined

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b bg-background px-4 py-2">
        <Button variant="ghost" size="sm" onClick={() => nav('/automation')} title={t('flow.backToList')}><ArrowLeft className="h-4 w-4" /></Button>
        <Input value={ten} onChange={(e) => setTen(e.target.value)} disabled={chiXem} className="h-8 w-72 font-semibold" />
        <label className="flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] text-muted-foreground" title={t('flow.priorityTitle')}>
          {t('flow.priority')}
          <input type="number" value={uuTien} disabled={chiXem} onChange={(e) => setUuTien(Math.trunc(Number(e.target.value) || 0))}
            className="h-6 w-12 rounded border-0 bg-transparent px-1 text-right text-xs text-foreground outline-none focus:ring-1 focus:ring-ring" />
        </label>
        <div className="flex items-center gap-1.5 rounded-md border px-2 py-1" title={t('flow.enableTitle')}>
          {!chiXem && <Switch checked={rule.enabled} disabled={update.isPending} onCheckedChange={batTat} />}
          <span className={cn('text-[11px] font-medium', rule.enabled ? 'text-success' : 'text-muted-foreground')}>{rule.enabled ? t('flow.on') : t('flow.off')}</span>
        </div>
        {chuaLuu && <Badge variant="warning" className="text-[10px]">{t('flow.unsaved')}</Badge>}
        {chiXem && <Badge variant="secondary" className="text-[10px]">{t('flow.viewOnly')}</Badge>}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {!chiXem && !moAi && <OAiGoiY onClick={() => setMoAi(true)} />}
          {!chiXem && coTheHoanTac && <Button size="sm" variant="ghost" onClick={hoanTacAi} title={t('flow.undoAiTitle')}><Undo2 className="h-3.5 w-3.5" /> {t('flow.undoAi')}</Button>}
          {!chiXem && <Button size="sm" variant="outline" onClick={() => setMoMau(true)}><Sparkles className="h-3.5 w-3.5" /> {t('flow.templates')}</Button>}
          {!chiXem && <Button size="sm" variant="outline" onClick={() => setMoChayThu(true)} disabled={testRun.isPending}>{testRun.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FlaskConical className="h-3.5 w-3.5" />} {t('flow.testRun')}</Button>}
          {run && <Button size="sm" variant="ghost" onClick={() => setMoVet(true)} title={t('flow.viewTrace')}><Badge variant={KET_QUA_LABEL[run.ketQua].variant} className="text-[10px]">{KET_QUA_LABEL[run.ketQua].ten}</Badge></Button>}
          {run && <Button size="sm" variant="ghost" onClick={xoaVet} title={t('flow.clearTrace')}><X className="h-3.5 w-3.5" /></Button>}
          <Button size="sm" variant="ghost" onClick={() => setMoNhatKy(true)}><History className="h-3.5 w-3.5" /> {t('flow.log')}</Button>
          {!chiXem && <Button size="sm" variant="outline" onClick={() => luu()} disabled={update.isPending}><Save className="h-3.5 w-3.5" /> {t('common:actions.save')}</Button>}
          {!chiXem && !rule.enabled && <Button size="sm" onClick={() => luu(true)} disabled={update.isPending}><Save className="h-3.5 w-3.5" /> {t('flow.saveAndEnable')}</Button>}
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Bảng khối */}
        {!chiXem && (
          <aside className={cn('shrink-0 space-y-1 overflow-y-auto border-r bg-muted/20 p-2 transition-[width]', moThanh ? 'w-48' : 'w-11')}>
            <button type="button" onClick={() => setMoThanh((v) => !v)} aria-expanded={moThanh} title={moThanh ? t('flow.sidebar.collapse') : t('flow.sidebar.expand')} className={cn('flex items-center rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground', moThanh ? 'ml-auto' : 'mx-auto')}>
              {moThanh ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
            </button>
            {moThanh && <>
            <p className="px-1 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">{t('flow.sidebar.trigger')}</p>
            <button type="button" onClick={() => setMoKichHoat(true)} className="flex w-full items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-2 py-1.5 text-left text-[11px] text-primary hover:bg-primary/15" title={t('flow.sidebar.changeTrigger')}>
              <VongIcon icon={tm.icon} mau={tm.mau} size="h-6 w-6" /><span className="min-w-0 flex-1 leading-tight">{tm.ten}</span>
            </button>
            <p className="px-1 pt-2 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">{t('flow.sidebar.addBlock')}</p>
            <button type="button" onClick={() => setChonKhoi({ sau: sel?.id ?? null, handle: sel?.data.kind === 'condition' ? 'true' : undefined })} className="flex w-full items-center gap-2 rounded-md border border-primary/40 bg-primary/10 px-2 py-1.5 text-left text-xs font-medium text-primary hover:bg-primary/15" title={sel ? t('flow.sidebar.connectAfter', { name: sel.data.label }) : t('flow.sidebar.addToCanvas')}>
              <LayoutGrid className="h-3.5 w-3.5" /> {t('flow.sidebar.library')} <Plus className="ml-auto h-3 w-3" />
            </button>
            {([['condition', t('flow.sidebar.condition'), GitBranch], ['delay', t('flow.sidebar.delay'), Clock]] as const).map(([k, nhan, I]) => (
              <button key={k} type="button" onClick={() => them({ kind: k })} className="flex w-full items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-left text-xs hover:bg-accent/60" title={sel ? t('flow.sidebar.connectAfter', { name: sel.data.label }) : t('flow.sidebar.addToCanvas')}>
                <I className="h-3.5 w-3.5 text-muted-foreground" /> {nhan} <Plus className="ml-auto h-3 w-3 text-muted-foreground" />
              </button>
            ))}
            <p className="px-1 pt-1 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">{t('flow.sidebar.quick')}</p>
            {HANH_DONG_NHANH.map((a) => { const M = actionMeta(a); const I = M.icon; return (
              <button key={a} type="button" onClick={() => them({ kind: 'action', actionType: a })} className="flex w-full items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-left text-xs hover:bg-accent/60">
                <I className="h-3.5 w-3.5 text-muted-foreground" /> <span className="truncate">{M.ten}</span> <Plus className="ml-auto h-3 w-3 shrink-0 text-muted-foreground" />
              </button>
            ) })}
            <p className="px-1 pt-2 text-[10.5px] text-muted-foreground">{t('flow.sidebar.help')}</p>
            </>}
          </aside>
        )}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div ref={khungRef} className="relative min-h-0 min-w-0 flex-1">
            <CanvasCtx.Provider value={{
              themSau: (nid, handle) => setChonKhoi({ sau: nid, handle }),
              doiKichHoat: () => setMoKichHoat(true),
              enabled: rule.enabled,
              toggle: batTat,
              chiXem,
            }}>
              <ReactFlow
                nodes={nodes} edges={edges} nodeTypes={nodeTypes}
                onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
                onSelectionChange={({ nodes: ns }) => setSelId(ns[0]?.id ?? null)}
                nodesDraggable={!chiXem} nodesConnectable={!chiXem}
                fitViewOptions={{ padding: 0.15, maxZoom: 1 }} minZoom={0.2}
                deleteKeyCode={chiXem ? null : ['Backspace', 'Delete']}
              >
                <Background gap={16} /><Controls showInteractive={false} /><MiniMap pannable zoomable nodeStrokeWidth={2} />
              </ReactFlow>
            </CanvasCtx.Provider>
          </div>
        </div>

        {/* Trợ lý AI: cột đứng bên phải; mở từ ô gợi ý trên header, tắt thì ô gợi ý hiện lại */}
        {moAi && !chiXem && (
          <aside className="flex w-[22rem] shrink-0 flex-col border-l bg-background p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-sm font-semibold"><WandSparkles className="h-4 w-4 text-primary" /> {t('composer.title')}</p>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" title={t('flow.ai.close')} onClick={() => setMoAi(false)}><X className="h-3.5 w-3.5" /></Button>
            </div>
            <AiComposerPanel
              className="min-h-0 flex-1"
              currentFlow={() => (fcHienTai && fcHienTai.nodes.length > 0 ? fcHienTai : null)}
              applyLabel={nodes.length > 1 ? t('flow.ai.apply') : t('flow.ai.applyCanvas')}
              onApply={apDungAi}
            />
          </aside>
        )}

        {/* Bảng cấu hình — chỉ hiện khi đang chọn một khối, để canvas dùng hết bề ngang */}
        {sel && (
          <aside className="w-[22rem] shrink-0 overflow-y-auto border-l bg-background p-3">
            <CauHinhKhoi
              key={sel.id}
              node={sel} triggerType={triggerType} ruleId={rule.id} truongMoRong={truongMoRong} chiXem={chiXem}
              buocGanNhat={buocCuaSel} capNhat={capNhatSel} doiKichHoat={() => setMoKichHoat(true)} xoa={xoaSel} dong={boChon}
            />
          </aside>
        )}
      </div>

      <Dialog open={moVet} onOpenChange={setMoVet}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{t('flow.trace.title')} {run && <Badge variant={KET_QUA_LABEL[run.ketQua].variant}>{KET_QUA_LABEL[run.ketQua].ten}</Badge>}</DialogTitle>
            <DialogDescription>{run?.khach ? t('flow.trace.withContact', { name: run.khach }) : t('flow.trace.sample')}{t('flow.trace.note')}</DialogDescription>
          </DialogHeader>
          {run && <ChiTietLanChay run={run} onChonKhoi={(nid) => { setSelId(nid); setNodes((ns) => ns.map((n) => ({ ...n, selected: n.id === nid }))) }} />}
        </DialogContent>
      </Dialog>

      <ChayThuDialog open={moChayThu} onClose={() => setMoChayThu(false)} triggerType={triggerType} dangChay={testRun.isPending} onChay={chayThu} />
      <NhatKyDialog open={moNhatKy} onClose={() => { setMoNhatKy(false); if (params.get('nhatky')) setParams({}, { replace: true }) }} ruleId={rule.id} tenLuong={ten || rule.name} kinds={kinds} />
      <ChonMauDialog open={moMau} onClose={() => setMoMau(false)} onChon={apMau} canhBao={nodes.length > 1 ? t('flow.template.warn') : undefined} />
      <ChonKhoiDialog open={!!chonKhoi} onClose={() => setChonKhoi(null)} onChon={(k) => them(k, { id: chonKhoi?.sau ?? null, handle: chonKhoi?.handle })} />
      <ChonKichHoatDialog open={moKichHoat} hienTai={triggerType} onClose={() => setMoKichHoat(false)} onChon={doiKichHoat} />

      <Dialog open={blocker.state === 'blocked'} onOpenChange={(v) => { if (!v) blocker.reset?.() }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('flow.leave.title')}</DialogTitle><DialogDescription>{t('flow.leave.desc')}</DialogDescription></DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => blocker.reset?.()}>{t('flow.leave.stay')}</Button>
            <Button variant="destructive" onClick={() => blocker.proceed?.()}>{t('flow.leave.go')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
